import React from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { AlertCircle, Home, RefreshCw } from "lucide-react";

const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
    const handleReload = () => {
        window.location.href = "/"
    }

    const handleRefresh = () => {
        window.location.reload()
    }

    return (
        <div className="relative min-h-screen bg-background text-foreground overflow-hidden flex items-center justify-center p-4">
            {/* Animated background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-secondary/5 pointer-events-none" />

            {/* Decorative elements */}
            <div className="absolute top-10 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse" />
            <div
                className="absolute bottom-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl animate-pulse"
                style={{ animationDelay: "1s" }}
            />

            {/* Content */}
            <div className="relative z-10 w-full max-w-2xl">
                <div className="backdrop-blur-sm bg-card/40 border border-border rounded-2xl p-8 md:p-12 shadow-2xl">
                    {/* Icon */}
                    <div className="flex justify-center mb-8">
                        <div className="relative">
                            <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl" />
                            <AlertCircle className="relative w-16 h-16 text-primary animate-bounce" />
                        </div>
                    </div>

                    {/* Heading */}
                    <h1 className="text-4xl md:text-5xl font-bold text-center mb-4 text-balance">Something went wrong</h1>

                    {/* Description */}
                    <p className="text-center text-muted-foreground text-lg mb-2">
                        We encountered an unexpected issue while loading this page.
                    </p>

                    {/* Error message */}
                    {/* <div className="mt-6 p-4 bg-destructive/5 border border-destructive/20 rounded-lg mb-8">
                        <p className="text-sm font-mono text-muted-foreground break-all">
                            {error.message || "Unknown error occurred"}
                        </p>
                    </div> */}

                    {/* Action buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                            onClick={handleReload}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-all duration-200 hover:shadow-lg active:scale-95"
                        >
                            <Home className="w-5 h-5" />
                            Go to Home
                        </button>
                        <button
                            onClick={handleRefresh}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium rounded-lg transition-all duration-200 hover:shadow-lg active:scale-95"
                        >
                            <RefreshCw className="w-5 h-5" />
                            Try Again
                        </button>
                    </div>

                    {/* Footer note */}
                    <p className="text-center text-xs text-muted-foreground mt-8">
                        If this problem persists, please contact support team hello@elystrion.com
                    </p>
                </div>
            </div>
        </div>
    )
};

const AppErrorBoundary = ({ children }: { children: React.ReactNode }) => (
    <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(error, info) => {
            console.error("Error caught by ErrorBoundary:", error, info);
        }}
    >
        {children}
    </ErrorBoundary>
);

export default AppErrorBoundary;
